import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { __, _i } from "../../utils";
import { SameSectionAccordion, SETTING_ACCESSIBILITY_CHANGED_EVENT } from "@devowl-wp/customize";
import { observer } from "mobx-react";
import { Result, Tag } from "antd";
import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
function createHierarchy(settings, showPassed) {
    const root = {};
    const removeUnderscoreProperty = [];
    for(const strKey in settings){
        const setting = settings[strKey];
        const { breadcrumb, valid } = setting;
        if (valid && !showPassed) {
            continue;
        }
        const parts = breadcrumb.split(" ▸ ").map((part)=>part.trim());
        let current = root;
        for (const part of parts){
            if (!current[part]) {
                current[part] = {
                    __: setting
                };
                removeUnderscoreProperty.push(current[part]);
            }
            current = current[part];
        }
    }
    for (const remove of removeUnderscoreProperty){
        const { __, ...rest } = remove;
        if (Object.keys(rest).length > 0) {
            delete remove.__;
        }
    }
    return root;
}
const TreeNode = (param)=>{
    let { node } = param;
    if (Object.keys(node).length === 0) {
        return null;
    }
    return /*#__PURE__*/ _jsx("ul", {
        style: {
            listStyle: "disc",
            paddingLeft: 20
        },
        children: Object.keys(node).map((key)=>{
            const { __ } = node[key];
            return /*#__PURE__*/ _jsx("li", {
                style: __ ? {
                    listStyle: "none",
                    marginLeft: -20
                } : undefined,
                children: __ ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        __.valid ? /*#__PURE__*/ _jsx(CheckCircleFilled, {
                            style: {
                                color: "green"
                            }
                        }) : /*#__PURE__*/ _jsx(ExclamationCircleFilled, {
                            style: {
                                color: "#df3b3b"
                            }
                        }),
                        "\xa0",
                        /*#__PURE__*/ _jsx("a", {
                            href: "#",
                            onClick: (e)=>{
                                __.instance.focus();
                                e.preventDefault();
                            },
                            children: key
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        key,
                        /*#__PURE__*/ _jsx(TreeNode, {
                            node: node[key]
                        })
                    ]
                })
            }, key);
        })
    });
};
const CustomizeA11yScore = observer(()=>{
    const [open, setOpen] = useState(false);
    const [settings, setSettings] = useState({});
    const [showPassed, setShowPassed] = useState(false);
    // Also, allow autofocus by a customAutofocus (this is not a standard!)
    useEffect(()=>{
        if (window.location.href.indexOf("customAutofocus[rcb-a11y-score]=1") > -1) {
            setOpen(true);
        }
    }, []);
    // Listen to `useA11yDispatcher` and save all "catched" accessibility properties in `settings`
    useEffect(()=>{
        document.addEventListener(SETTING_ACCESSIBILITY_CHANGED_EVENT, (param)=>{
            let { detail } = param;
            if (detail.setting.startsWith("rcb-")) {
                setSettings((s)=>({
                        ...s,
                        [detail.setting]: detail
                    }));
            }
        });
    }, []);
    const values = Object.values(settings).filter((param)=>{
        let { visible } = param;
        return visible;
    });
    values.sort((param, param1)=>{
        let { breadcrumb: a } = param;
        let { breadcrumb: b } = param1;
        return a.localeCompare(b);
    });
    const { score, max: scoreMax } = values.reduce((p, param)=>{
        let { valid } = param;
        p.max++;
        if (valid) {
            p.score++;
        }
        return p;
    }, {
        max: 0,
        score: 0
    });
    const percent = score / scoreMax * 100;
    const scoreColor = percent >= 95 ? "green" : percent >= 50 ? "orange" : "red";
    return /*#__PURE__*/ _jsxs(SameSectionAccordion, {
        state: open,
        onToggle: ()=>setOpen(!open),
        title: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                __("Accessibility score"),
                " ",
                /*#__PURE__*/ _jsxs(Tag, {
                    color: scoreColor,
                    children: [
                        percent.toFixed(0),
                        " %"
                    ]
                })
            ]
        }),
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "description",
                children: _i(__("Reach 100 points to meet all of Real Cookie Banner's recommendations for making your cookie banner be easily accessible to people with disabilities on the basis of the {{a}}WCAG Standard 2.2{{/a}}. You can find out which sections of your cookie banner need to be changed and which improvements we suggest under the individual options in the customizer."), {
                    a: /*#__PURE__*/ _jsx("a", {
                        href: __("https://www.w3.org/TR/WCAG22/"),
                        target: "_blank",
                        rel: "noreferrer"
                    })
                })
            }),
            percent === 100 ? /*#__PURE__*/ _jsx(Result, {
                status: "success",
                subTitle: __("Congratulations, your Cookie Banner is accessible! Thank you for making your cookie banner easily accessible to people with disabilities.")
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "description",
                        children: __("The following configurations do not comply with the requirements:")
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        style: {
                            textAlign: "right"
                        },
                        children: /*#__PURE__*/ _jsx("a", {
                            onClick: ()=>setShowPassed(!showPassed),
                            children: showPassed ? __("Hide passed rules") : __("Show passed rules")
                        })
                    }),
                    /*#__PURE__*/ _jsx(TreeNode, {
                        node: createHierarchy(values, showPassed)
                    })
                ]
            })
        ]
    });
});
export { CustomizeA11yScore };
